import { Link } from 'react-router';
import LegalPageLayout from '../components/legal/LegalPageLayout';
import { LegalEmail, Term, Value } from '../components/legal/LegalPrimitives';
import { legalConfig, legalDates } from '../config/legal';

/**
 * DISCLOSURE LEVEL: these Terms state the rules of using ShareShutter. They do
 * not describe how it is built. No pipeline stages, no derivative generation,
 * no access-control mechanics, no link or identifier design. See the same note
 * in PrivacyPolicyPage.jsx.
 *
 * These Terms still must not claim a capability the product lacks. Where one is
 * missing, the copy states the route that actually exists (email) rather than
 * inventing an in-app one. The gap list itself is internal:
 * docs/LEGAL_PRELAUNCH_GAPS.md.
 */
const sections = [
  {
    id: 'acceptance',
    title: 'Accepting these terms',
    content: (
      <>
        <p>
          These Terms of Use are an agreement between you and{' '}
          <Value>{legalConfig.legalEntity}</Value> (<Term>ShareShutter</Term>,{' '}
          <Term>we</Term>, <Term>us</Term>, <Term>our</Term>) covering the ShareShutter
          mobile applications, website, web console and the services behind them, which we
          call the <Term>Services</Term>.
        </p>
        <p>
          You agree to these Terms when you access the Services, create an account, join a
          collection, upload a photo or video, or make a purchase. Our{' '}
          <Link to="/privacy">Privacy Policy</Link> explains how we handle your
          information and forms part of this agreement.
        </p>
        <p>If you do not agree to these Terms, please do not use the Services.</p>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: 'Who may use ShareShutter',
    content: (
      <>
        <p>
          To use the Services you must be at least{' '}
          <Value>{legalConfig.minimumAge}</Value> and able to enter into a binding
          agreement where you live. If you are under the age of majority in your country
          but at or above the minimum age, you may use the Services only with the
          involvement of a parent or guardian who agrees to these Terms on your behalf.
        </p>
        <p>
          You may not use the Services if we have previously suspended or removed your
          account, or if doing so would break a law that applies to you.
        </p>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Your account',
    content: (
      <>
        <p>You are responsible for your account. That means:</p>
        <ul>
          <li>Giving accurate information when you sign up, and keeping it current.</li>
          <li>Keeping your password confidential and not sharing your account.</li>
          <li>
            Everything that happens under your account, whether or not you authorised it.
          </li>
          <li>
            Telling us promptly at <LegalEmail address={legalConfig.supportEmail} /> if
            you believe someone else has gained access to it.
          </li>
          <li>
            Not impersonating anyone else, including by choosing a username or profile
            photo intended to make people think you are someone you are not.
          </li>
        </ul>
        <p>
          We may suspend or restrict an account where we reasonably believe it is
          necessary for security, to deal with abuse, or to comply with the law. See{' '}
          <a href="#suspension">Suspension and termination</a>.
        </p>
      </>
    ),
  },
  {
    id: 'hosts-and-guests',
    title: 'Hosts and guests',
    content: (
      <>
        <p>There are two roles in a collection:</p>
        <ul>
          <li>
            <strong>The host</strong> creates the collection, chooses its plan, sets its
            name, description, cover and event date, invites people, and can close or
            delete it.
          </li>
          <li>
            <strong>Guests</strong> join through a link or a QR code. Guests can view the
            collection, upload photos and videos to it, share the invitation, and see who
            else has joined.
          </li>
        </ul>
        <p>
          Hosts are responsible for who they invite and how widely they distribute an
          invitation, and for the collection they run. Everyone is responsible for what
          they upload.
        </p>
      </>
    ),
  },
  {
    id: 'your-content',
    title: 'Your content stays yours',
    content: (
      <>
        <p>
          <Term>User Content</Term> means the photos, videos, collection covers,
          collection names and descriptions, profile photos, usernames and anything else
          you upload or submit to the Services.
        </p>
        <p>
          <strong>You keep ownership of your User Content.</strong> These Terms do not
          transfer any ownership of your photos or videos to us. We do not claim
          authorship of them, we do not sell them, and we do not license them to anyone
          else.
        </p>
      </>
    ),
  },
  {
    id: 'licence',
    title: 'The permission you give us to run the service',
    content: (
      <>
        <p>
          To operate ShareShutter we need your permission to do the technical things a
          photo sharing service does. By uploading User Content you grant us a
          non-exclusive, worldwide, royalty-free licence to host, store, reproduce,
          transmit, display and make technical modifications to it, such as reformatting
          and resizing, and to make it available to the people authorised to be in the
          relevant collection.
        </p>
        <p>This permission is limited in scope. It exists solely to provide, secure,
        maintain and improve the Services, and it is subject to your collection settings
        and our <Link to="/privacy">Privacy Policy</Link>.</p>
        <p>
          The licence ends when the content is removed from the Services, except that
          copies may remain in backups and caches for a period, and we may retain content
          where we are required to by law or where it is needed to resolve a dispute or
          enforce these Terms.
        </p>
        <p>
          You also grant other members of a collection the ability to view the User
          Content you add to it, which is the point of a shared collection.
        </p>
      </>
    ),
  },
  {
    id: 'your-promises',
    title: 'What you promise about what you upload',
    content: (
      <>
        <p>Each time you upload User Content, you confirm that:</p>
        <ul>
          <li>
            You own it, or you have all the rights and permissions needed to upload it and
            to grant the permission described above.
          </li>
          <li>
            It does not infringe anyone's copyright, trademark, privacy, publicity or
            other rights.
          </li>
          <li>
            Where the law requires it, you have the consent of people who appear in it,
            and you are respecting the reasonable expectations of anyone recognisable in
            it.
          </li>
          <li>It complies with these Terms and with the laws that apply to you.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'What is not allowed',
    content: (
      <>
        <p>Do not use the Services to upload, share or do any of the following.</p>
        <p>
          <strong>Content that is never acceptable:</strong>
        </p>
        <ul>
          <li>Anything unlawful, or that promotes or facilitates unlawful activity.</li>
          <li>
            Child sexual abuse material, or any sexual content involving a minor. We
            report this to the authorities.
          </li>
          <li>
            Intimate images shared without the consent of the person shown, and content
            created to sexualise or degrade someone.
          </li>
          <li>Threats, harassment, stalking, or content intended to intimidate someone.</li>
          <li>
            Hate speech, or content attacking people on the basis of who they are.
          </li>
          <li>Content that infringes someone else's intellectual property.</li>
          <li>
            Content that invades someone's privacy, including recordings made where people
            reasonably expect not to be recorded.
          </li>
          <li>Malware, or files disguised as photos or videos.</li>
        </ul>
        <p>
          <strong>Conduct that is not allowed:</strong>
        </p>
        <ul>
          <li>Impersonating another person, or misrepresenting your connection to anyone.</li>
          <li>
            Fraud, phishing, or attempting to obtain other people's account credentials.
          </li>
          <li>
            Trying to reach a collection you have not been invited to, or to get around
            any access control or limit.
          </li>
          <li>
            Publishing a collection invitation somewhere it will reach people the host did
            not intend to include, or otherwise misusing an invitation.
          </li>
          <li>
            Scraping, bulk downloading, or accessing the Services by automated means
            except as we expressly permit.
          </li>
          <li>
            Probing, overloading, disrupting or reverse engineering the Services or the
            infrastructure behind them.
          </li>
          <li>Reselling or commercially exploiting the Services without our agreement.</li>
        </ul>
        <p>
          If you are not sure whether something is allowed, the safer choice is not to
          upload it.
        </p>
      </>
    ),
  },
  {
    id: 'reporting',
    title: 'Reporting problems and content moderation',
    content: (
      <>
        <p>
          If you come across content or behaviour in ShareShutter that breaks these Terms,
          tell us at <LegalEmail address={legalConfig.supportEmail} /> with enough detail
          to find it, such as the collection and what you saw.
        </p>
        <p>
          We review what is reported to us. Where content or conduct breaks these Terms we
          may remove or restrict the content, warn the account, suspend or terminate it,
          and in serious cases report it to law enforcement. We take repeated or severe
          violations more seriously. We do not review everything uploaded to ShareShutter
          and we do not promise to catch everything, but we act on what is reported.
        </p>
        <p>
          A host can delete a collection they run, which removes access to everything in
          it.
        </p>
      </>
    ),
  },
  {
    id: 'copyright',
    title: 'Copyright complaints',
    content: (
      <>
        <p>
          If you believe content on ShareShutter infringes your copyright, write to{' '}
          <LegalEmail address={legalConfig.copyrightEmail} /> and include: your contact
          details; identification of the work you say is infringed; enough information for
          us to locate the content; a statement that you believe in good faith the use is
          not authorised by the rights holder or the law; a statement that the information
          in your notice is accurate; and your physical or electronic signature.
        </p>
        <p>
          We remove or disable content in response to valid notices, and we may terminate
          the accounts of people who repeatedly infringe.
        </p>
      </>
    ),
  },
  {
    id: 'our-ip',
    title: 'Our intellectual property',
    content: (
      <>
        <p>
          The Services themselves belong to us: the software, the design, the ShareShutter
          name and logo, and everything else we provide other than User Content. We and
          our licensors keep all rights in them.
        </p>
        <p>
          You may use the Services as these Terms allow. You get no ownership of
          ShareShutter itself, and you may not copy, modify or create derivative works
          from the Services, or use our name or logo without our written permission.
        </p>
      </>
    ),
  },
  {
    id: 'invitations',
    title: 'Invitation links and QR codes',
    content: (
      <>
        <p>
          A collection invitation is a link to a particular collection, and a QR code is
          that same link in a form a camera can read. Anyone holding it can ask to join
          the collection, and can pass it on to someone else.
        </p>
        <p>
          <strong>Treat an invitation as public once you have distributed it.</strong> If
          you print a QR code and put it on a table, expect that anyone who sees it can
          use it. You are responsible for how you distribute an invitation, deliberately
          or accidentally, and for who ends up in a collection as a result. Except where
          the law provides otherwise, ShareShutter is not responsible for that
          distribution.
        </p>
      </>
    ),
  },
  {
    id: 'plans-and-limits',
    title: 'Plans, storage limits and expiry',
    content: (
      <>
        <p>Collections operate within limits, and you should know about them before you
        rely on one:</p>
        <ul>
          <li>Every collection has a storage allowance, and uploads are refused once it
          is used up.</li>
          <li>Individual files have size limits, and only supported photo and video
          formats are accepted.</li>
          <li>
            Collections have a lifetime tied to the plan, after which the collection
            expires and its content may become unavailable and be deleted.
          </li>
          <li>Some plans are limited to one active collection per host.</li>
        </ul>
        <p>
          The current allowance, file limits and duration for each plan are shown in the
          app when a collection is created. We describe them there rather than here so
          that this document does not go out of date.
        </p>
        <p>
          <strong>
            Export anything you want to keep before a collection expires.
          </strong>{' '}
          See <a href="#availability">Availability and loss of content</a>.
        </p>
      </>
    ),
  },
  {
    id: 'uploads-and-processing',
    title: 'How uploads are handled',
    content: (
      <>
        <p>
          An item may not appear in the collection the instant its upload finishes, and
          an upload that has finished may still turn out to be unusable.
        </p>
        <p>We may refuse or remove an upload that:</p>
        <ul>
          <li>Is in a format we do not support, or is not actually a photo or video.</li>
          <li>Is corrupt, truncated or cannot be decoded.</li>
          <li>Is larger than the applicable limit, or exceeds the collection's
          allowance.</li>
          <li>Appears to be malicious.</li>
          <li>Breaks these Terms.</li>
        </ul>
        <p>
          We cannot promise that every upload will succeed, or that a failed or rejected
          upload can be recovered. Keep the original on your device until you have
          confirmed the item appears in the collection.
        </p>
      </>
    ),
  },
  {
    id: 'payments',
    title: 'Plans, fees and payments',
    content: (
      <>
        <p>
          ShareShutter does not currently take payments. Where paid plans are offered in
          future, the price and what it includes will be shown before you buy, and prices
          may change for future purchases without affecting a purchase you have already
          made. You are responsible for any taxes that apply.
        </p>
        <p>
          Purchases made through the Apple App Store or Google Play are processed by
          Apple or Google under their terms. Purchases made elsewhere would be processed
          by a third-party payment provider; we would not receive your full card details.
        </p>
        <p>
          Where a plan is tied to a storage allowance and a retention period, those are
          what you are buying, and they are subject to the rest of these Terms including{' '}
          <a href="#availability">Availability and loss of content</a>.
        </p>
        <p>
          <strong>Refunds.</strong> Refunds are handled in accordance with the policy
          shown at the time of purchase and with the requirements of the relevant app
          store and applicable consumer law:{' '}
          <Value>{legalConfig.refundPolicy}</Value>. Nothing here limits refund rights you
          have by law.
        </p>
      </>
    ),
  },
  {
    id: 'app-stores',
    title: 'App store terms',
    content: (
      <>
        <p>
          If you download a ShareShutter application from the Apple App Store or Google
          Play, that store's terms also apply, and where they conflict with these Terms
          for that download, the store's terms govern to the extent of the conflict.
        </p>
        <p>
          Apple and Google are not parties to these Terms, are not responsible for the
          Services, and have no obligation to provide support for them. Where required by
          Apple, you agree that Apple is a third-party beneficiary of these Terms and may
          enforce them against you.
        </p>
      </>
    ),
  },
  {
    id: 'third-parties',
    title: 'Third-party services',
    content: (
      <p>
        ShareShutter runs on infrastructure from other companies and may link to services
        we do not control. We are not responsible for third-party services, their content
        or their availability, and their own terms and privacy policies will apply when
        you use them.
      </p>
    ),
  },
  {
    id: 'changes-to-service',
    title: 'Changes to the Services',
    content: (
      <p>
        We may add, change or remove features,
        adjust which media formats are supported, carry out maintenance, and change
        pricing for future purchases. Where a change would materially reduce something you
        have already paid for, we will act consistently with applicable law and with what
        you bought. We will give notice of significant changes where we reasonably can.
      </p>
    ),
  },
  {
    id: 'suspension',
    title: 'Suspension and termination',
    content: (
      <>
        <p>
          We may suspend or terminate your access to the Services, or to a particular
          collection, where we reasonably believe it is necessary because of a breach of
          these Terms, unlawful activity, abuse or harm to others, a security threat,
          non-payment where fees apply, or a legal requirement. Where it is practical and
          lawful to do so, we will tell you why.
        </p>
        <p>
          When an account or a collection is terminated, access to the User Content in it
          ends, and that content may be deleted. We do not promise to store it or to
          return it to you afterwards, so please keep your own copies.
        </p>
      </>
    ),
  },
  {
    id: 'your-termination',
    title: 'Ending your use of ShareShutter',
    content: (
      <>
        <p>
          You can stop using the Services at any time. A host can delete a collection they
          run from the collection's menu, which removes access to it and to the media in
          it.
        </p>
        <p>
          To close your account, email{' '}
          <LegalEmail address={legalConfig.supportEmail} /> from the address on the
          account.
        </p>
      </>
    ),
  },
  {
    id: 'availability',
    title: 'Availability and loss of content',
    content: (
      <>
        <p>
          <strong>
            ShareShutter is a way to gather and share the photos and videos from an event.
            It is not a backup service and it is not a permanent archive.
          </strong>
        </p>
        <p>We cannot guarantee that the Services will be uninterrupted, that processing
        will always succeed, that content will remain available indefinitely, or that a
        particular upload can be recovered. Content can become unavailable or be lost
        because of:</p>
        <ul>
          <li>Outages or failures at the providers whose infrastructure we run on.</li>
          <li>Network or internet failures.</li>
          <li>Software defects and hardware failures.</li>
          <li>Deletion by you or by a collection's host.</li>
          <li>A collection reaching the end of its plan and expiring.</li>
          <li>File corruption, or an upload that fails part way.</li>
          <li>Compromise of an account.</li>
          <li>Maintenance, and events outside our reasonable control.</li>
        </ul>
        <p>
          We use commercially reasonable efforts to run the Services reliably and to look
          after what you entrust to us.{' '}
          <strong>
            Please keep independent copies of any photo or video that matters to you, and
            download what you want to keep before a collection expires.
          </strong>{' '}
          To the maximum extent permitted by applicable law, we are not liable for content
          that is lost or becomes unavailable.
        </p>
      </>
    ),
  },
  {
    id: 'warranties',
    title: 'Disclaimer of warranties',
    content: (
      <>
        <p>
          To the maximum extent permitted by applicable law, the Services are provided{' '}
          <Term>as is</Term> and <Term>as available</Term>, without warranties of any
          kind, whether express or implied, including implied warranties of
          merchantability, fitness for a particular purpose, title and non-infringement.
          We do not warrant that the Services will meet your requirements, be
          uninterrupted or error free, or that any defect will be corrected.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain warranties or the
          limitation of statutory consumer rights. Nothing in these Terms excludes or
          limits rights you have that cannot lawfully be excluded or limited, and in those
          places the exclusions above apply only to the extent permitted.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    content: (
      <>
        <p>
          To the maximum extent permitted by applicable law, ShareShutter and its
          officers, employees and suppliers will not be liable for any indirect,
          incidental, special, consequential, exemplary or punitive damages, or for loss
          of profits, revenue, goodwill or business, or for loss of or damage to content
          or data, whether or not we were told such damages were possible.
        </p>
        <p>
          To the maximum extent permitted by applicable law, we will not be liable for
          unauthorised access to your account or content, the conduct of other users or
          third parties, interruptions or outages of the Services, or failures of
          processing, storage or delivery.
        </p>
        <p>
          Our total liability arising out of or relating to the Services is limited to{' '}
          {legalConfig.liabilityCap ? (
            <>{legalConfig.liabilityCap}</>
          ) : (
            <Value>[LIABILITY CAP]</Value>
          )}
          .
        </p>
        <p>
          Nothing in this section limits liability for death or personal injury caused by
          negligence, for fraud, or for anything else that cannot lawfully be limited. If
          applicable law does not allow some of these limitations, they apply to you only
          to the extent permitted.
        </p>
      </>
    ),
  },
  {
    id: 'indemnity',
    title: 'Indemnification',
    content: (
      <p>
        To the extent permitted by applicable law, you agree to indemnify and hold
        ShareShutter harmless from third-party claims, damages and reasonable costs arising
        out of User Content you upload, your infringement of someone else's rights, your
        misuse of the Services, or your breach of these Terms. We will tell you about any
        such claim and will not settle it in a way that imposes an obligation on you
        without your agreement. This does not apply to the extent a claim arises from our
        own acts or omissions.
      </p>
    ),
  },
  {
    id: 'force-majeure',
    title: 'Events outside our control',
    content: (
      <p>
        We are not responsible for failure or delay in providing the Services caused by
        events beyond our reasonable control, including failures or outages at our cloud
        and infrastructure providers, internet and network failures, power failures,
        natural disasters, fire, epidemics, war, civil unrest, government action, labour
        disputes, and significant cyber attacks. This does not excuse an obligation that
        cannot lawfully be excused, including refunding amounts paid for something we
        cannot provide.
      </p>
    ),
  },
  {
    id: 'disputes',
    title: 'Governing law and disputes',
    content: (
      <>
        <p>
          These Terms are governed by the laws of{' '}
          <Value>{legalConfig.governingLaw}</Value>, without regard to its conflict of
          laws rules. Disputes will be brought in the courts of{' '}
          <Value>{legalConfig.venue}</Value>, and you and we consent to their
          jurisdiction. Nothing here removes a right you have to bring proceedings in your
          country of residence where the law gives you that right.
        </p>
        <p>
          If something goes wrong, please contact us first at{' '}
          <LegalEmail address={legalConfig.supportEmail} />. Most problems are resolved
          quickly that way, and we would rather fix an issue than argue about it.
        </p>
      </>
    ),
  },
  {
    id: 'changes-to-terms',
    title: 'Changes to these terms',
    content: (
      <p>
        We may update these Terms as ShareShutter changes. When we do, we will update the
        Last Updated date at the top of this page, and we will give reasonable advance
        notice of material changes where applicable law requires it. If you keep using the
        Services after a change takes effect, you accept the updated Terms to the extent
        the law allows. If you do not agree to a change, stop using the Services and
        contact us.
      </p>
    ),
  },
  {
    id: 'general',
    title: 'General',
    content: (
      <ul>
        <li>
          <strong>Severability.</strong> If a provision is found unenforceable, it is
          modified to the least extent necessary or removed, and the rest stays in force.
        </li>
        <li>
          <strong>No waiver.</strong> If we do not enforce a provision, we have not given
          it up.
        </li>
        <li>
          <strong>Assignment.</strong> You may not transfer these Terms without our
          consent. We may transfer them to an affiliate or in connection with a merger,
          acquisition or sale of assets.
        </li>
        <li>
          <strong>Entire agreement.</strong> These Terms and the{' '}
          <Link to="/privacy">Privacy Policy</Link> are the whole agreement between you
          and us about the Services and replace any earlier understanding about them.
        </li>
      </ul>
    ),
  },
  {
    id: 'contact',
    title: 'Contact us',
    content: (
      <>
        <p>Questions about these Terms:</p>
        <address className="legal__address">
          <span>ShareShutter</span>
          <span>
            <Value>{legalConfig.legalEntity}</Value>
          </span>
          <span>
            <Value>{legalConfig.mailingAddress}</Value>
          </span>
          <span>
            Support: <LegalEmail address={legalConfig.supportEmail} />
          </span>
          <span>
            Copyright: <LegalEmail address={legalConfig.copyrightEmail} />
          </span>
        </address>
      </>
    ),
  },
];

export default function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms of Use"
      lastUpdated={legalDates.terms}
      meta={{
        title: 'ShareShutter Terms of Use',
        description:
          'Review the terms that apply when using ShareShutter to create, join, and contribute to shared event collections.',
        canonicalPath: '/terms',
      }}
      intro={
        <p>
          These terms cover using ShareShutter to create collections, invite guests, and
          share photos and videos. We have kept them as plain as we can.
        </p>
      }
      sections={sections}
    />
  );
}
